import React, { useState, useEffect, useRef } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import { toast } from "react-toastify";
import Select from "react-select";
import BaseModal from "../../components/common/BaseModal/BaseModal";
import ErrorMessage from "../../components/common/ErrorMessage/ErrorMessage";
import { DropdownIndicator } from '../../components/common/SelectComponents';
import { getSelectStyles } from '../../utils/selectStyles';
// import { formatErrorMessage } from "../../utils/formatErrorMessage";
import { connectionService } from "../../services/apiService";
import { prepareConnectionPayload } from "../../utils/encrypt";

const EditConnectionModal = ({
    isOpen,
    onClose,
    connectionData,
    onSuccess,
    setIsLoading,
}) => {
    const authTypeOptions = [
        { value: "password", label: "Password" },
        { value: "private_key", label: "Private Key" },
    ];

    const initialState = {
        connectionName: "",
        accountIdentifier: "",
        username: "",
        warehouse: "",
        database: "",
        role: "",
        passwordType: "",
        enterPassword: "",
        privateKey: "",
        privateKeyFileName: "",
        keyPassphrase: "",
    };

    const [formData, setFormData] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [passwordFieldsTouched, setPasswordFieldsTouched] = useState({
        enterPassword: false,
        privateKey: false,
        keyPassphrase: false,
    });
    const [isTested, setIsTested] = useState(false);
    const [isConnectionSuccessfull, setIsConnectionSuccessfull] = useState(false);
    const [serverPublicKey, setServerPublicKey] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    // const [showPrivateKey, setShowPrivateKey] = useState(false);
    const [showPassphrase, setShowPassphrase] = useState(false);
    const fileInputRef = useRef(null);

    // Fetch server public key on component mount
    useEffect(() => {
        const fetchPublicKey = async () => {
            try {
                const publicKey = await connectionService.getPublicKey();
                setServerPublicKey(publicKey);
            } catch (err) {
                console.error("Failed to fetch server public key:", err);
            }
        };

        fetchPublicKey();
    }, []);

    // Populate form with connection data when modal opens
    useEffect(() => {
        if (isOpen && connectionData) {
            // Map auth_type to passwordType for the form
            let passwordType = "";
            if (connectionData?.auth_type === "password") {
                passwordType = "password";
            } else if (connectionData?.auth_type === "keypair") {
                passwordType = "private_key";
            }

            setFormData({
                connectionName: connectionData?.name || "",
                accountIdentifier: connectionData?.account || "",
                username: connectionData?.user || "",
                warehouse: connectionData?.warehouse || "",
                database: connectionData?.database || "",
                role: connectionData?.role || "",
                passwordType: passwordType,
                // Show exactly 5 asterisks for masked fields - now editable
                enterPassword: connectionData?.auth_type === "password" ? "•••••" : "",
                privateKey: connectionData?.auth_type === "keypair" ? "•••••" : "",
                privateKeyFileName: "",
                // Show asterisks only if has_passphrase is true, otherwise empty
                keyPassphrase: connectionData?.has_passphrase ? "•••••" : "",
            });
            // Reset touched state when modal opens
            setPasswordFieldsTouched({
                enterPassword: false,
                privateKey: false,
                keyPassphrase: false,
            });

            setIsTested(connectionData?.is_tested || false);
        }
    }, [isOpen, connectionData]);

    const getRequiredFields = () => {
        const baseFields = [
            "connectionName",
            "accountIdentifier",
            "username",
            "warehouse",
            "database",
            "role",
            "passwordType",
        ];

        // If password fields have been modified (not just asterisks), validate them
        if (
            formData.passwordType === "password"
            // && passwordFieldsTouched.enterPassword
        ) {
            return [...baseFields, "enterPassword"];
        } else if (
            formData.passwordType === "private_key"
            // && passwordFieldsTouched.privateKey
        ) {
            return [...baseFields, "privateKey"];
        }

        return baseFields;
    };

    const validateField = (field, value) => {
        const requiredFields = getRequiredFields();

        if (requiredFields.includes(field) && !value.trim()) {
            const fieldName = field
                .replace("enterPassword", "Password")
                .replace("privateKey", "Private Key")
                .replace("passwordType", "Authentication Type")
                .replace(/([A-Z])/g, " $1")
                .trim();
            return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
        }

        return "";
    };

    const handleChange = (field, value) => {
        // setFormData((prev) => ({ ...prev, [field]: value })); // check this

        setFormData(prev => {
            const updated = { ...prev, [field]: value };

            // Only clear passphrase if private key is being cleared (not being typed)
            if (field === 'privateKey') {
                updated.keyPassphrase = '';
            }

            return updated;
        });

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: "" }));
        }

        // Reset isTested when form data changes
        if (isTested) {
            setIsTested(false);
        }
    };

    const handlePasswordFieldChange = (field, value) => {
        // Mark field as touched when user modifies it
        setPasswordFieldsTouched((prev) => ({ ...prev, [field]: true }));
        handleChange(field, value);
    };

    const handlePasswordFieldFocus = (field) => {
        // Clear the asterisks when user focuses on the field
        if (formData[field] === "•••••") {
            setFormData((prev) => ({ ...prev, [field]: "" }));
            // Mark as touched when user focuses on the field with asterisks
            setPasswordFieldsTouched((prev) => ({ ...prev, [field]: true }));
        }
    };

    const handlePasswordFieldBlur = (field) => {
        // If user leaves the field empty, restore asterisks if original connection had this credential
        if (formData[field] === "") {
            let shouldRestoreAsterisks = false;

            if (field === "enterPassword") {
                shouldRestoreAsterisks =
                    connectionData && connectionData?.auth_type === "password";
            } else if (field === "privateKey") {
                shouldRestoreAsterisks =
                    connectionData && connectionData?.auth_type === "keypair";
                // Also restore passphrase if private key is being restored
                if (shouldRestoreAsterisks && connectionData?.has_passphrase) {
                    setFormData((prev) => ({ ...prev, keyPassphrase: "•••••" }));
                    setPasswordFieldsTouched((prev) => ({ ...prev, keyPassphrase: false }));
                }
            } else if (field === "keyPassphrase") {
                shouldRestoreAsterisks =
                    connectionData && connectionData?.has_passphrase;
            }

            if (shouldRestoreAsterisks) {
                setFormData((prev) => ({ ...prev, [field]: "•••••" }));
                // Reset touched state when restoring asterisks
                setPasswordFieldsTouched((prev) => ({ ...prev, [field]: false }));
            }
        }
    };

    const handlePasswordTypeChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            passwordType: value,
            enterPassword:
                connectionData && connectionData?.auth_type === "password"
                    ? "•••••"
                    : "",
            privateKey:
                connectionData && connectionData?.auth_type === "keypair"
                    ? "•••••"
                    : "",
            privateKeyFileName: "",
            keyPassphrase:
                connectionData && connectionData?.has_passphrase ? "•••••" : "",
        }));

        // Reset touched state when password type changes
        setPasswordFieldsTouched({
            enterPassword: false,
            privateKey: false,
            keyPassphrase: false,
        });

        // Clear related errors
        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.passwordType;
            delete newErrors.enterPassword;
            delete newErrors.privateKey;
            delete newErrors.keyPassphrase;
            return newErrors;
        });

        // Reset isTested when password type changes
        if (isTested) {
            setIsTested(false);
        }

        // Reset the toggle icon in password, private_key and key_passphrase
        setShowPassword(false);
        // setShowPrivateKey(false);
        setShowPassphrase(false);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const fileContent = event.target.result;

                // Check character length
                if (fileContent.length > 2048) {
                    toast.error("File content must be less than 2048 characters.");
                    e.target.value = ""; // reset file input
                    return;
                }

                setFormData((prev) => ({
                    ...prev,
                    privateKey: fileContent,
                    privateKeyFileName: file.name,
                    keyPassphrase: ''
                }));

                setPasswordFieldsTouched((prev) => ({ ...prev, privateKey: true }));

                // Clear any existing error for privateKey
                if (errors.privateKey) {
                    setErrors(prev => ({ ...prev, privateKey: '' }));
                }

                // Reset isTested when file changes
                if (isTested) {
                    setIsTested(false);
                }
            };
            reader.onerror = () => {
                toast.error("Failed to read file. Please try again.");
            };
            reader.readAsText(file);
        }
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const handleClearFile = () => {
        setFormData((prev) => ({
            ...prev,
            // privateKey: "",
            privateKey:
                connectionData && connectionData?.auth_type === "keypair" ? "•••••" : "",
            privateKeyFileName: "",
            keyPassphrase:
                connectionData && connectionData?.has_passphrase ? "•••••" : "",
        }));
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        // Reset touched state when clearing file
        setPasswordFieldsTouched((prev) => ({ ...prev, privateKey: true }));

        // Reset isTested when file is cleared
        if (isTested) {
            setIsTested(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;
        const requiredFields = getRequiredFields();

        requiredFields.forEach((field) => {
            const error = validateField(field, formData[field]);
            if (error) {
                newErrors[field] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    // Prepare API payload according to schema
    const preparePayload = (type) => {
        const payload = {
            name: formData.connectionName,
            account: formData.accountIdentifier,
            user: formData.username,
            role: formData.role,
            warehouse: formData.warehouse,
            database: formData.database,
            database_type: "snowflake",
            is_connected_successfully: isConnectionSuccessfull,
            ...(type === 'update' && { is_tested: isTested }),
        };

        // Set auth_type and credentials based on passwordType
        if (formData.passwordType === "password") {
            payload.auth_type = "password";
            // Only include password if it was modified (not asterisks)
            if (
                passwordFieldsTouched.enterPassword &&
                formData.enterPassword !== "•••••"
            ) {
                payload.password = formData.enterPassword;
            } else {
                payload.password = null; // Keep existing password
            }
            payload.private_key_pem = null;
            payload.passphrase = null;
        } else if (formData.passwordType === "private_key") {
            payload.auth_type = "keypair";
            payload.password = null;
            // Only include private key if it was modified (not asterisks)
            if (passwordFieldsTouched.privateKey && formData.privateKey !== "•••••") {
                payload.private_key_pem = formData.privateKey;
            } else {
                payload.private_key_pem = null; // Keep existing key
            }
            // Only include passphrase if it was modified
            if (
                passwordFieldsTouched.keyPassphrase &&
                formData.keyPassphrase !== "•••••"
            ) {
                payload.passphrase = formData.keyPassphrase || null;
            } else {
                payload.passphrase = null; // Keep existing passphrase
            }
        }

        return payload;
    };

    const handleTestConnection = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        const payload = preparePayload('test');

        try {
            // Encrypt payload before sending
            const encryptedPayload = await prepareConnectionPayload(
                payload,
                serverPublicKey,
            );

            // Make API call to test updated connection
            const response = await connectionService.testUpdatedConnection(
                connectionData?.id,
                encryptedPayload,
            );

            if (response.success) {
                // Set isTested to true on success
                setIsTested(true);
                setIsConnectionSuccessfull(true);
                toast.success("Test successful");
            } else {
                setIsTested(true);
                setIsConnectionSuccessfull(false);
                toast.error(response.message || "Test failed");
            }
            // Call onSuccess callback to refresh connections list
            // await onSuccess?.();
        } catch (error) {
            // setIsTested(false);
            toast.error(
                error.message ||
                "Connection test failed. Please check your credentials.",
            );
            console.error("Test updated connection error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        const payload = preparePayload('update');

        try {
            // Delay of 150ms
            // await new Promise((resolve) => { setTimeout(resolve, 150) });

            // Encrypt payload before sending
            const encryptedPayload = await prepareConnectionPayload(
                payload,
                serverPublicKey,
            );

            // Make API call to update connection
            const response = await connectionService.updateConnection(
                connectionData?.id,
                encryptedPayload,
            );
            if (response) {
                toast.success("Connection updated successfully");

                // Call onSuccess callback to refresh connections list
                await onSuccess?.();

                // Reset form
                setErrors({});
                setFormData(initialState);
                setPasswordFieldsTouched({
                    enterPassword: false,
                    privateKey: false,
                    keyPassphrase: false,
                });
                setIsTested(false);
                onClose();
            }
        } catch (error) {
            toast.error(
                error.message || "Failed to update connection. Please try again.",
            );
            console.error("Update connection error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleModalClose = () => {
        // setErrors({});
        // setFormData(initialState);
        // setPasswordFieldsTouched({
        //     enterPassword: false,
        //     privateKey: false,
        //     keyPassphrase: false,
        // });
        // setIsTested(false);

        // // Reset the toggle icon in password, private_key and key_passphrase
        // setShowPassword(false);
        // // setShowPrivateKey(false);
        // setShowPassphrase(false);
        onClose();
    };

    const footer = (
        <>
            <button
                type="button"
                className="btn btn-cancel"
                onClick={handleModalClose}
            >
                Cancel
            </button>
            <button
                type="button"
                className="btn btn-secondary"
                onClick={handleTestConnection}
            >
                Test Connection
            </button>
            <button type="submit" className="btn btn-primary" onClick={handleSubmit}>
                Update Connection
            </button>
        </>
    );

    // Determine if we should show textarea (manual entry) or input (file uploaded)
    const showTextarea = !formData.privateKeyFileName;

    return (
        <>
            <BaseModal
                isOpen={isOpen}
                onClose={handleModalClose}
                title="Edit Connection"
                footer={footer}
            >
                <form onSubmit={handleSubmit}>
                    {/* Connection Name - Full Width */}
                    <div className="form-row">
                        <div className="form-group form-group-full">
                            <label>
                                Connection Name <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="e.g., Production - Primary DB"
                                value={formData.connectionName}
                                disabled
                            />
                        </div>
                    </div>

                    {/* Account Identifier & Warehouse */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>
                                Account Identifier <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="e.g., xy12345.us-east-1"
                                value={formData.accountIdentifier}
                                onChange={(e) =>
                                    handleChange("accountIdentifier", e.target.value)
                                }
                                className={errors.accountIdentifier ? "error" : ""}
                            />
                            <ErrorMessage message={errors.accountIdentifier} />
                        </div>

                        <div className="form-group">
                            <label>
                                Warehouse <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="e.g., COMPUTE_WH"
                                value={formData.warehouse}
                                onChange={(e) => handleChange("warehouse", e.target.value)}
                                className={errors.warehouse ? "error" : ""}
                            />
                            <ErrorMessage message={errors.warehouse} />
                        </div>
                    </div>

                    {/* Database & Role */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>
                                Database <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="e.g., PROD_DB"
                                value={formData.database}
                                onChange={(e) => handleChange("database", e.target.value)}
                                className={errors.database ? "error" : ""}
                            />
                            <ErrorMessage message={errors.database} />
                        </div>

                        <div className="form-group">
                            <label>
                                Role <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="e.g., SYSADMIN"
                                value={formData.role}
                                onChange={(e) => handleChange("role", e.target.value)}
                                className={errors.role ? "error" : ""}
                            />
                            <ErrorMessage message={errors.role} />
                        </div>
                    </div>

                    {/* Username & Password Type */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>
                                Username <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Enter Username"
                                autoComplete="off"
                                value={formData.username}
                                onChange={(e) => handleChange("username", e.target.value)}
                                className={errors.username ? "error" : ""}
                            />
                            <ErrorMessage message={errors.username} />
                        </div>

                        <div className="form-group">
                            <label>
                                Authentication Type <span className="required">*</span>
                            </label>
                            <Select
                                options={authTypeOptions}
                                value={authTypeOptions.find((opt) => opt.value === formData.passwordType) || null}
                                onChange={(selected) => handlePasswordTypeChange(selected ? selected.value : "")}
                                placeholder="Select Authentication Type"
                                styles={getSelectStyles(!!errors.passwordType)}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                isSearchable={false}
                                components={{ DropdownIndicator }}
                            />
                            <ErrorMessage message={errors.passwordType} />
                        </div>
                    </div>

                    {/* Conditional Fields Based on Password Type */}
                    {formData.passwordType === "password" && (
                        <div className="form-row">
                            <div className="form-group">
                                <label>
                                    Enter Password <span className="required">*</span>
                                </label>
                                {/* <input
                                    type="text"
                                    placeholder="Enter Password"
                                    value={formData.enterPassword}
                                    onChange={(e) =>
                                        handlePasswordFieldChange("enterPassword", e.target.value)
                                    }
                                    onFocus={() => handlePasswordFieldFocus("enterPassword")}
                                    onBlur={() => handlePasswordFieldBlur("enterPassword")}
                                    className={errors.enterPassword ? "error" : ""}
                                /> */}
                                <div style={{ position: "relative" }}>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Enter Password"
                                        autoComplete="new-password"
                                        value={formData.enterPassword}
                                        onChange={(e) =>
                                            handlePasswordFieldChange("enterPassword", e.target.value)
                                        }
                                        onFocus={() => handlePasswordFieldFocus("enterPassword")}
                                        onBlur={() => handlePasswordFieldBlur("enterPassword")}
                                        className={errors.enterPassword ? "error" : ""}
                                        style={{
                                            paddingRight:
                                                (passwordFieldsTouched.enterPassword ||
                                                    formData.enterPassword === "") &&
                                                    (formData.enterPassword !== "•••••" ||
                                                        passwordFieldsTouched.enterPassword)
                                                    ? "2.083vw"
                                                    : "0.729vw",
                                        }}
                                    />
                                    {(passwordFieldsTouched.enterPassword ||
                                        formData.enterPassword === "") &&
                                        (formData.enterPassword !== "•••••" ||
                                            passwordFieldsTouched.enterPassword) && (
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{
                                                    position: "absolute",
                                                    right: "0.521vw",
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    background: "none",
                                                    border: "none",
                                                    cursor: "pointer",
                                                    padding: "0.208vw",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    color: "#64748b",
                                                }}
                                                title={showPassword ? "Show password" : "Hide password"}
                                            >
                                                {showPassword ? (
                                                    <Eye style={{ width: "0.938vw", height: "0.938vw" }} />
                                                ) : (
                                                    <EyeOff style={{ width: "0.938vw", height: "0.938vw" }} />
                                                )}
                                            </button>
                                        )}
                                </div>
                                <ErrorMessage message={errors.enterPassword} />
                            </div>
                        </div>
                    )}

                    {formData.passwordType === "private_key" && (
                        <>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>
                                        Private Key <span className="required">*</span>
                                    </label>
                                    <div className="input-with-button-and-clear">
                                        {showTextarea ? (
                                            <textarea
                                                rows={1}
                                                type="text"
                                                placeholder="Enter Private Key or Upload File"
                                                value={formData.privateKey}
                                                onChange={(e) =>
                                                    handlePasswordFieldChange("privateKey", e.target.value)
                                                }
                                                onFocus={() => handlePasswordFieldFocus("privateKey")}
                                                onBlur={() => handlePasswordFieldBlur("privateKey")}
                                                className={errors.privateKey ? "error" : ""}
                                            />
                                            // <div style={{ position: 'relative', gridColumn: '1' }}>
                                            //     <textarea
                                            //         rows={1}
                                            //         type="text"
                                            //         placeholder="Enter Private Key or Upload File"
                                            //         value={showPrivateKey ? formData.privateKey : formData.privateKey.replace(/./g, '•')}
                                            //         onChange={(e) => {
                                            //             if (showPrivateKey) {
                                            //             handlePasswordFieldChange('privateKey', e.target.value);
                                            //             }
                                            //         }}
                                            //         onFocus={() => handlePasswordFieldFocus("privateKey")}
                                            //         onBlur={() => handlePasswordFieldBlur("privateKey")}
                                            //         className={errors.privateKey ? 'error' : ''}
                                            //         disabled={!!formData.privateKeyFileName}
                                            //         style={{ paddingRight: ((passwordFieldsTouched.privateKey || formData.privateKey === "") && (formData.privateKey !== "•••••" || passwordFieldsTouched.privateKey)) ? '40px' : '14px' }}
                                            //     />
                                            //     {(passwordFieldsTouched.privateKey || formData.privateKey === "") && (formData.privateKey !== "•••••" || passwordFieldsTouched.privateKey) && (
                                            //         <button
                                            //             type="button"
                                            //             onClick={() => setShowPrivateKey(!showPrivateKey)}
                                            //             style={{
                                            //                 position: 'absolute',
                                            //                 right: '10px',
                                            //                 top: '8px',
                                            //                 background: 'none',
                                            //                 border: 'none',
                                            //                 cursor: 'pointer',
                                            //                 padding: '4px',
                                            //                 display: 'flex',
                                            //                 alignItems: 'center',
                                            //                 color: '#64748b'
                                            //             }}
                                            //             title={showPrivateKey ? 'Show private key' : 'Hide private key'}
                                            //         >
                                            //             {showPrivateKey ? <Eye size={18} /> : <EyeOff size={18} />}
                                            //         </button>)}
                                            // </div>
                                        ) : (
                                            <input
                                                type="text"
                                                value={formData.privateKeyFileName}
                                                onChange={(e) =>
                                                    handleChange("privateKey", e.target.value)
                                                }
                                                className={errors.privateKey ? "error" : ""}
                                                disabled={!!formData.privateKeyFileName}
                                            />
                                        )}
                                        {/* <input
                                            type="text"
                                            placeholder="Enter Private Key or Upload File"
                                            value={formData.privateKeyFileName || formData.privateKey}
                                            onChange={(e) => handlePasswordFieldChange('privateKey', e.target.value)}
                                            onFocus={() => handlePasswordFieldFocus('privateKey')}
                                            className={errors.privateKey ? 'error' : ''}
                                            disabled={!!formData.privateKeyFileName}
                                        /> */}
                                        {formData.privateKeyFileName && (
                                            <button
                                                type="button"
                                                className="clear-file-btn"
                                                onClick={handleClearFile}
                                                title="Clear uploaded file"
                                            >
                                                <X style={{ width: "0.833vw", height: "0.833vw" }} />
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="browse-btn"
                                            onClick={handleBrowseClick}
                                        >
                                            Browse
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            onChange={handleFileUpload}
                                            style={{ display: "none" }}
                                            accept=".pem,.p8,.pub,.txt"
                                        />
                                    </div>
                                    <ErrorMessage message={errors.privateKey} />
                                </div>

                                <div className="form-group">
                                    <label>Key Passphrase</label>
                                    {/* <input
                                        type="text"
                                        placeholder="Enter Key Passphrase"
                                        value={formData.keyPassphrase}
                                        onChange={(e) =>
                                            handlePasswordFieldChange("keyPassphrase", e.target.value)
                                        }
                                        onFocus={() => handlePasswordFieldFocus("keyPassphrase")}
                                        onBlur={() => handlePasswordFieldBlur("keyPassphrase")}
                                    /> */}
                                    <div style={{ position: "relative" }}>
                                        <input
                                            type={showPassphrase ? "text" : "password"}
                                            placeholder="Enter Key Passphrase"
                                            autoComplete="new-password"
                                            value={formData.keyPassphrase}
                                            onChange={(e) =>
                                                handlePasswordFieldChange(
                                                    "keyPassphrase",
                                                    e.target.value,
                                                )
                                            }
                                            onFocus={() => handlePasswordFieldFocus("keyPassphrase")}
                                            onBlur={() => handlePasswordFieldBlur("keyPassphrase")}
                                            style={{
                                                paddingRight:
                                                    (passwordFieldsTouched.keyPassphrase ||
                                                        formData.keyPassphrase === "") &&
                                                        (formData.keyPassphrase !== "•••••" ||
                                                            passwordFieldsTouched.keyPassphrase)
                                                        ? "2.083vw"
                                                        : "0.729vw",
                                            }}
                                        />
                                        {(passwordFieldsTouched.keyPassphrase ||
                                            formData.keyPassphrase === "") &&
                                            (formData.keyPassphrase !== "•••••" ||
                                                passwordFieldsTouched.keyPassphrase) && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassphrase(!showPassphrase)}
                                                    style={{
                                                        position: "absolute",
                                                        right: "0.521vw",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        background: "none",
                                                        border: "none",
                                                        cursor: "pointer",
                                                        padding: "0.208vw",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        color: "#64748b",
                                                    }}
                                                    title={
                                                        showPassphrase
                                                            ? "Show passphrase"
                                                            : "Hide passphrase"
                                                    }
                                                >
                                                    {showPassphrase ? (
                                                        <Eye style={{ width: "0.938vw", height: "0.938vw" }} />
                                                    ) : (
                                                        <EyeOff style={{ width: "0.938vw", height: "0.938vw" }} />
                                                    )}
                                                </button>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </form>
            </BaseModal>
        </>
    );
};

export default EditConnectionModal;
